import * as React from 'react';
import TimeTaxSupportSystemClient from '../../../system';
import MessageCourier from '../../../lib/MessageCourier';
import { PanelCompaniesSelector } from './PanelCompaniesSelector';
import { DateTime } from 'luxon';
import { ICommandBarItemProps, PrimaryButton } from '@fluentui/react';
import { buttonStylesForProjects, defaultCommandBarButtonStyles } from '../../../styles/button-styles';
import { useNavigate } from 'react-router-dom';
import { routes } from '../../../common/constants';
import ViewWrapperWithCommandBar from '../../Layout/ViewWrapperWithCommandBar';
import { User } from '../../../system/User';
import { useTranslation } from 'react-i18next';
import { HoursDashboardChart } from './HoursDashboardChart';
import { CompanyAreaMetrics } from '../../../system/panel/interfaces/companyAreaMetrics';
import { OptionEmpresa } from '../../../shared/components/EmpresaSelector';

interface ICompanyPanel {
  system: TimeTaxSupportSystemClient;
  courier: MessageCourier;
  changeBlockUI: (change: boolean) => void;
  user: User;
  useAllSector?: boolean;
}

export const PanelCompanies = (props: ICompanyPanel) => {
  const { t } = useTranslation();
  const [organizationNodeId, setOrganizationNodeId] = React.useState<string | null>(null);
  const [company, setCompany] = React.useState<OptionEmpresa | null>(null);
  const [fromDate, setFromDate] = React.useState<DateTime | undefined>(undefined);
  const [paisId, setPaisId] = React.useState<string | null>(null);
  const [data, setData] = React.useState<CompanyAreaMetrics | null>(null);
  const [hasSearched, setHasSearched] = React.useState(false);
  const [companySubmit, setCompanySubmit] = React.useState<OptionEmpresa | null>(null);

  const hasData = !!data && (data.areaMetrics.length > 0 || data.companyMetrics.length > 0);

  const navigate = useNavigate();

  const getPanelsData = async () => {
    if (!fromDate) return;
    if (!company) {
      props.courier.messageWarning(t('Debe seleccionar una empresa'));
      return;
    }

    try {
      props.courier.messageWarning('');
      props.changeBlockUI(true);

      const response = await props.system.getPanelSystem().companyAreaMetricsReport(fromDate, paisId, organizationNodeId, [company.key]);
      setCompanySubmit(company)
      setData(response);
      setHasSearched(true);
    } catch (error) {
      console.log(error);
    } finally {
      props.changeBlockUI(false);
    }
  };

  const onClickSearch = async () => {
    getPanelsData();
  };

  const setSelectedOrganizationNodeId = (id: string) => {
    setOrganizationNodeId(id);
  };

  const setSelectedCompanyId = (company: OptionEmpresa | null) => {
    setCompany(company);
  };

  const setFrom = (date: DateTime) => {
    setFromDate(date);
  };

  const items = React.useMemo(
    (): ICommandBarItemProps[] => [
      {
        key: 'project',
        text: t('Mi Panel'),
        onClick: () => navigate(`${routes.dashboard_own}`),
        itemProps: {},
        iconProps: { iconName: 'BIDashboard' },
        buttonStyles: defaultCommandBarButtonStyles,
      },
      {
        key: 'download',
        text: t('Avance de proyectos'),
        onClick: () => navigate(`${routes.dashboard}`),
        iconProps: { iconName: 'TeamWork' },
        buttonStyles: defaultCommandBarButtonStyles,
      },
      {
        key: 'avance_por_sector',
        text: t('Avance por sector'),
        onClick: () => navigate(`${routes.dashboard_avance_por_sector}`),
        iconProps: { iconName: 'BarChart4' },
        buttonStyles: defaultCommandBarButtonStyles,
      },
      {
        key: 'panel_companies',
        text: t('Empresas'),
        onClick: () => navigate(`${routes.dashboard_empresas}`),
        iconProps: { iconName: 'CityNext2' },
        buttonStyles: buttonStylesForProjects,
      },
      {
        key: 'colabs',
        text: t('Colaboradores'),
        onClick: () => navigate(`${routes.dashboard_colaboradores}`),
        iconProps: { iconName: 'ReminderPerson' },
        buttonStyles: defaultCommandBarButtonStyles,
      },
    ],
    [navigate],
  );

  const downloadExcel = async () => {
    if (!company) {
      props.courier.messageWarning(t('Debe seleccionar una empresa'));
      return;
    }

    if (fromDate) {
      try {
        props.changeBlockUI(true);
        const uri = props.system.getPanelSystem().getDownloadCompanyAreaMetricsReportExcelURI(fromDate, paisId, organizationNodeId, [company.key]);
        props.changeBlockUI(false);
        window.open(uri, '_blank');
        props.courier.messageSuccess(t('Se ha descargado el archivo con éxito'));
      } catch (error) {
        console.log(error);
      } finally {
        props.changeBlockUI(false);
      }
    }
  };

  return (
    <ViewWrapperWithCommandBar
      withBorderBottom
      title={''}
      items={items}
      overflowButtonStyles={defaultCommandBarButtonStyles}
      // overflowItems={overflowItems}
      variant="highligth"
      titleBody={t('Panel')}
    >
      <div className="ms-Grid padding-1" dir="ltr">
        <PanelCompaniesSelector
          user={props.user}
          system={props.system}
          courier={props.courier}
          onChangeCompany={(company) => {
            setSelectedCompanyId(company);
          }}
          onChangeOrganizationNodeId={setSelectedOrganizationNodeId}
          onChangePais={(pais) => {
            setPaisId(pais ? pais.key : null);
          }}
          onChangeFromDate={setFrom}
          onClickSearch={onClickSearch}
          useAllSector={true}
          showPeriodFilter={true}
        />
      </div>

      <div className="padding-1">
        {hasData && (
          <div
            className="mb-2"
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <PrimaryButton
              text={t('Exportar')}
              iconProps={{
                iconName: 'ExcelLogo',
              }}
              onClick={downloadExcel}
            />
          </div>
        )}

        {hasData && fromDate && companySubmit && (
          <HoursDashboardChart
            period={fromDate}
            company={companySubmit}
            areas={data.areaMetrics.map((a) => ({
              area: a.nor_nombre,
              hours: Number(a.total_horas),
            }))}
            countries={data.companyMetrics.map((c) => ({
              country: c.pais_nombre,
              cost: Number(c.total_importe),
              hours: Number(c.total_horas),
            }))}
          />
        )}

        {hasSearched && !hasData && (
          <div
            style={{
              textAlign: 'center',
              padding: '48px 24px',
              background: '#fff',
              borderRadius: 8,
              border: '1px solid #edebe9',
            }}
          >
            <i
              className="ms-Icon ms-Icon--SearchIssue"
              style={{
                fontSize: 48,
                marginBottom: 16,
                display: 'block',
                color: '#605e5c',
              }}
            />

            <h3>{t('No se encontraron datos')}</h3>

            <p
              style={{
                color: '#605e5c',
                marginTop: 8,
              }}
            >
              {t('No existen registros para los filtros seleccionados. Intenta cambiar el período, país, empresa o área.')}
            </p>
          </div>
        )}
      </div>
    </ViewWrapperWithCommandBar>
  );
};
